/**
 * Sender-domain policy for Gmail receipt sync.
 *
 * Three sets, each with a distinct behaviour:
 *
 * 1. NEVER_RETAIL_DOMAINS: sender is ALWAYS out-of-scope. Reject before we
 *    spend LLM tokens. Covers BNPL platforms, payment platforms, warehouse
 *    clubs, home improvement, automotive dealers, telecoms, SaaS, pet /
 *    pharmacy chains, travel, pure shipment trackers, etc. If a merchant
 *    lands here, the prompt's corresponding category rule applies.
 *
 * 2. GENERIC_EMAIL_PROVIDER_DOMAINS: sender is the user's own email (forwarded
 *    mail). Merchant MUST come from the body.
 *
 * 3. SENDER_NOT_MERCHANT: sender is a POS processor (Square, SumUp, Zettle,
 *    Cardpointe, ebizcharge, etc.) or a consumer receipt aggregator
 *    (your-receipt.com, senser.net, ereceiptsdillards.com). The body IS a
 *    real retail receipt for an underlying brand — do NOT hard-block.
 *    Merchant MUST come from the body, same treatment as generic email
 *    providers.
 *
 * Everything else flows through to the LLM triage + extraction prompts.
 */

const NEVER_RETAIL_DOMAINS = new Set([
  // Corporate billing / dev infra (original members)
  'shopmyshelf.us',
  'shopmy.com',
  'stripe.com',
  'ramp.com',
  'brex.com',
  'paypal.com',
  'venmo.com',
  'cashapp.com',
  'squareup.com',
  'docusign.net',
  'ironcladapp.com',

  // Pure shipment trackers / customer-messaging platforms (prompt rule #12).
  // NOTE: receipt aggregators like your-receipt.com, senser.net,
  // ereceiptsdillards.com are NOT in this set — they forward real receipts.
  // They belong in SENDER_NOT_MERCHANT below.
  'narvar.com',
  'podium.email',
  'store-emails.com',

  // Payment / BNPL / installment platforms (prompt rule #2).
  // These duplicate the underlying merchant's own receipt, so even though the
  // body contains a real merchant, counting both double-counts.
  'klarna.com',
  'klarna.se',
  'klarna.de',
  'afterpay.com',
  'sezzle.com',
  'affirm.com',
  'tabby.ai',
  'clearpay.co.uk',
  'cashstar.com',
  'zip.co',
  'paypal.co.uk',
  'paypal.com.au',
  'splitit.com',
  'link.com',
  'synchrony.com',
  'myfortiva.com',
  'revolut.com',

  // Gift card / cashback / loyalty platforms (prompt rule #16).
  'raiseright.com',
  'capitaloneshopping.com',
  'wgiftcard.com',
  'thegiftcardshop.com',
  'oneloyaltynetwork.com',
  'perfectgift.com',
  'givex.com',
  'biltrewards.com',
  'egifter.com',
  'giftcardmall.com',

  // Telecom / utility / ISP (prompt rule #9).
  'att.com',
  'att.net',
  'att-mail.com',
  'verizon.com',
  'verizon.net',
  'verizonwireless.com',
  't-mobile.com',
  'tmobile.com',
  'visible.com',
  'comcast.net',
  'xfinity.com',
  'spectrumemails.com',
  'charter.net',
  'bellsouth.net',
  'shaw.ca',
  'sky.com',
  'frontier.com',
  'uscellular.com',
  'pseg.com',

  // Automotive (prompt rule #11).
  'carvana.com',
  'gubagoo.com',
  'autonation.com',
  'westsidelexus.com',
  'tesla.com',
  'ford.com',
  'bmwusa.com',
  'harley-davidson.com',
  'email-carmax.com',
  'autozone.com',
  'oreillyauto.com',
  'carparts.com',
  'pelicanparts.com',
  'detroitaxle.com',
  'autodent.co',
  'cardinale.com',
  'quirkcars.com',
  'realtruck.com',

  // B2B SaaS / digital / app store / account notifications (prompt rule #10).
  'appleid.com',
  'squarespace.info',
  'netsuite.com',
  'zendesk.com',
  'canva.com',
  'workhuman.com',
  'microsoft.com',
  'onmicrosoft.com',
  'facebook.com',
  'meta.com',
  'google.com',
  'godaddy.com',
  'shopify.com',
  'gorgias.com',
  'recurly.com',
  'zoho.com',
  'myheritage.com',
  'mindbodyonline.com',
  'logitech.com',
  'hp.com',
  'dell.com',
  'samsung.com',
  'remarkable.com',
  'nintendo.net',
  'discord.com',
  'kobo.com',
  'dotdigital-email.com',
  'sailthru.com',
  'digistore24.com',
  'wufoo.com',
  'stan.store',
  'samcart.com',
  'printify.com',
  'printful.com',
  'printful.me',
  'brother.com',
  'manh.com',
  'hbr.org',
  'blurb.com',
  'bookbaby.com',

  // Warehouse clubs / mass / dollar / discount-grocery (prompt rule #3).
  'walmart.com',
  'walmart.ca',
  'costco.com',
  'costco.ca',
  'samsclub.com',
  'bjs.com',
  'dollargeneral.com',
  'dollartree.com',
  'fivebelow.com',
  'familydollar.com',

  // Home improvement / appliances / lumber / building supply (prompt rule #7).
  'homedepot.com',
  'menards.com',
  'acehardware.com',
  'tractorsupply.com',
  'ferguson.com',
  'fergusonhome.com',
  'flooranddecor.com',
  'advanceplumbing.com',
  'geappliances.com',
  'applianceworld.com',
  'appliancecanada.com',
  'azappliancehome.com',
  'yaleappliance.com',
  'arnoldlumber.com',
  'fuseappliances.com',
  'abt.com',

  // Pet supplies / pet food (prompt rule #5).
  'chewy.com',
  'petsmart.com',
  'petco.com',
  'petsuppliesplus.com',
  'hollywoodfeed.com',
  'petsathome.com',
  'petvalu.ca',
  'petsense.com',
  'petfood.express',

  // Grocery / food delivery / rideshare (prompt rule #6).
  'doordash.com',
  'instacart.com',
  'uber.com',
  'postmates.com',
  'smithsfoodanddrug.com',
  'fredmeyer.com',
  'kroger.com',
  'wolt.com',
  'deliveroo.com',

  // Travel / hotels / airlines (prompt rule #8).
  'marriott.com',
  'airbnb.com',
  'qantas.com',
  'aspensnowmass.com',
  '1hotels.com',
  'halfdaytravel.com',

  // Shipping labels / couriers / storage (prompt rule #20).
  'usps.com',
  'fedex.com',
  'uhaul.com',

  // B2B wholesale marketplaces (prompt rule #21).
  'faire.com',
  'alibaba.com',
  'dhgate.com',

  // FSA / HSA marketplaces (prompt rule #22, sub-rule under #4).
  'hsastore.com',
  'fsastore.com',

  // Pharmacy / vision / contact lenses / dermatology / medspa (prompt rule #4).
  '1800contacts.com',
  'ezcontacts.com',
  'eyebuydirect.com',
  'lenscrafters.com',
  'sunglasshut.com',
  'contactsdirect.com',
  'glassesusa.com',
  'zennioptical.com',
  'eyeconic.com',
  'lensmartonline.com',
  'targetoptical.com',
  'opticontacts.com',
  'eyexamofca.com',
  'lensdirect.com',
  'framesdirect.com',
  'eyepromise.com',
  'designerframesoutlet.com',
  'glasses.com',
  'myeyedr.com',
  'lumeneyecenter.com',
  'americasbest.com',
  'mcfarlandeye.com',
  'colemandermatology.com',
  'skinspirit.com',
  'revance.com',
  'skinbetter.com',
  'galderma.com',
  'skinbeautifulrx.com',
  'skinmedica.com',
  'revisionskincare.com',
  'kilgourmd.com',
  'mindfuldermdallas.com',
  'westlakedermatology.com',
  'skinclique.com',
  'rescuespa.com',
  'drsturm.com',
  'facetdermatology.com',
  'drmarnie.com',
  'drmarinarosenthal.com',
  'drpatelct.com',
  'savvyderm.com',

  // School / yearbook / class-photo / fundraising / district domains (prompt rule #18).
  'jostens.com',
  'herffjones.com',
  'lifetouch.com',
  'treering.com',
  'studentreasures.com',
  'memorybook.com',
  'scholastic.com',
  'scholastic.ca',
  'scholastic.com.au',
  'kendallhunt.com',
  'follett.com',
  'mabelslabels.com',
  'bookshop.org',
  'nationalmahjonggleague.org',
  'globoforce.com',
  'universitycoop.com',
  'cheddarup.com',
  'givesmart.com',
  'snapraise.com',
  'onecause.com',
  'hostedbykg.com',
  'mypaymentsplus.com',
  'myschoolbucks.com',
  'schoolcashonline.com',
  'classwallet.com',
  'gradsupply.com',
  'schoolmerch.shop',
  'photopicsnow.com',
  'inter-state.com',
  'imprint.com',

  // Museums / non-profits / charities / gov (prompt rule #19).
  'goodwill.org',
  'noguchi.org',
  'moma.org',
  'churchofjesuschrist.org',
  'mountvernon.org',
  'salvationarmyusa.org',
  'salvationarmy.org',
  'skirball.org',
  'sundance.org',
  'nyhistory.org',
  'housingworks.org',
  'monticello.org',
  'whitney.org',
  'courtauld.ac.uk',
  'metmuseum.org',
  'si.edu',
  'artsbma.org',
  'usmint.gov',
  'girlscouts.org'
]);

/**
 * Senders whose From: domain is structurally NOT the merchant.
 *
 * Behaviour: do NOT block on sender. Instead the LLM must extract
 * merchantDomain from the email body — the body is branded for the
 * underlying retailer and contains the real receipt. If the body has no
 * identifiable merchant, fall back to rejection via the prompt rule (same
 * behaviour we already apply to generic-email-provider forwards).
 *
 * POS processors: the real retailer uses them as checkout infrastructure.
 * Receipt aggregators: the user opted in, receipts are forwarded verbatim.
 */
const SENDER_NOT_MERCHANT = new Set([
  // Receipt aggregators (consumer opt-in)
  'your-receipt.com',
  'senser.net',
  'ereceipts.co.uk',
  'ereceiptsdillards.com',

  // POS processors / e-commerce back-office
  'square.com',
  'sumup.com',
  'zettle.com',
  'cardpointe.com',
  'revelsystems.com',
  'ebizcharge.net',
  'cyncly.com',
  'swipesimple.com',
  'solapayments.com',
  'worldpay.com',
  'mxmerchant.com',
  'dejavoosystems.com',
  'getsafepay.com',
  'snappayglobal.com',
  'heartland.us',
  'erply.io',
  'cartpanda.com',
  'propay.com',
  'cityhive.net',
  'payxpert.com',
  'billing-notification.com',
  'intrum.com',
  'dustin.dk',
  '2c2p.com',
  'ecrebo.com',
  'nexi.it',
  'quanticpos.com',
  'moneris.com',
  'bambora.com',
  'payzen.eu',
  'sg-host.com',
  'channelreply.net'
]);

// Pure marketplaces/resale platforms whose merchant-domain receipts describe the checkout
// destination, not a brand the shopper prefers. Multi-brand retailers with useful catalog data
// are additionally detected by the recommendation pipeline's own-brand URL-share heuristic.
const MULTI_BRAND_MARKETPLACE_DOMAINS = new Set([
  'depop.com',
  'depop.app.link',
  'etsy.com',
  'ebay.com',
  'ebay.co.uk',
  'grailed.com',
  'goat.com',
  'mercari.com',
  'poshmark.com',
  'poshmark.ca',
  'stockx.com',
  'therealreal.com',
  'thredup.com',
  'us.vestiairecollective.com',
  'vestiairecollective.com',
  'vinted.com'
]);

// Generic email providers that show up in From: headers when an email is
// forwarded by the user. These are not merchants. Treat them as unreliable
// signals and fall back to the LLM's body-extracted merchantDomain.
const GENERIC_EMAIL_PROVIDER_DOMAINS = new Set([
  'gmail.com',
  'googlemail.com',
  'yahoo.com',
  'yahoo.co.uk',
  'yahoo.ca',
  'myyahoo.com',
  'ymail.com',
  'rocketmail.com',
  'outlook.com',
  'live.com',
  'hotmail.com',
  'hotmail.co.uk',
  'msn.com',
  'icloud.com',
  'me.com',
  'mac.com',
  'aol.com',
  'protonmail.com',
  'proton.me',
  'pm.me',
  'mail.com',
  'fastmail.com',
  'zoho.com',
  'gmx.com',
  'gmx.de',
  'qq.com',
  '163.com',
  '126.com'
]);

/**
 * Prefixes of generic email providers. Used to catch truncated/malformed
 * parses like "gmail.c", "gmail.co", "gmai", "gm" — all of which are
 * actually gmail.com but passed the simple isUnreliableHeaderDomain check
 * because they were > 3 chars and contained a dot (or were valid labels on
 * their own).
 */
const GENERIC_PROVIDER_PREFIXES = [
  'gmail',
  'googlemail',
  'yahoo',
  'ymail',
  'rocketmail',
  'outlook',
  'live',
  'hotmail',
  'msn',
  'icloud',
  'me',
  'mac',
  'aol',
  'protonmail',
  'proton',
  'pm',
  'fastmail',
  'gmx'
];

const normalizeReceiptDomain = domain => {
  if (!domain || typeof domain !== 'string') return '';
  return domain
    .replace(/^https?:\/\//i, '')
    .split('/')[0]
    .trim()
    .toLowerCase();
};

const isNeverRetailDomain = domain => {
  const normalizedDomain = normalizeReceiptDomain(domain);
  return normalizedDomain ? NEVER_RETAIL_DOMAINS.has(normalizedDomain) : false;
};

const isGenericEmailProviderDomain = domain => {
  const normalizedDomain = normalizeReceiptDomain(domain);
  return normalizedDomain ? GENERIC_EMAIL_PROVIDER_DOMAINS.has(normalizedDomain) : false;
};

const isSenderNotMerchantDomain = domain => {
  const normalizedDomain = normalizeReceiptDomain(domain);
  return normalizedDomain ? SENDER_NOT_MERCHANT.has(normalizedDomain) : false;
};

/**
 * Detect parses that look like a truncated generic-email-provider domain.
 * E.g. "gmail.c", "gmail.co", "gmai", "gm" all resolve true — they are all
 * malformed gmail.com parses observed in production. "gmail.co.uk" would
 * NOT match because it resolves to the full provider GENERIC_EMAIL_PROVIDER
 * via its own entry.
 */
const isTruncatedGenericProvider = domain => {
  const normalized = normalizeReceiptDomain(domain);
  if (!normalized) return false;
  // Already covered by isGenericEmailProviderDomain — let that path handle it.
  if (GENERIC_EMAIL_PROVIDER_DOMAINS.has(normalized)) return false;
  const firstLabel = normalized.split('.')[0];
  // Bare prefix without a dot (e.g. "gm", "gmai", "gma"): truncated.
  if (!normalized.includes('.')) {
    return GENERIC_PROVIDER_PREFIXES.some(
      prefix => prefix.startsWith(normalized) && prefix !== normalized
    );
  }
  // With a dot: e.g. "gmail.c", "gmail.co", "yahoo.c".
  // If first label matches a provider prefix exactly AND the whole domain is
  // not a recognised generic provider, it's a truncation.
  if (!GENERIC_PROVIDER_PREFIXES.includes(firstLabel)) return false;
  const lastLabel = normalized.split('.').pop() || '';
  // ".com" / ".co.uk" / ".de" etc. would already match an entry in
  // GENERIC_EMAIL_PROVIDER_DOMAINS — we short-circuited those above, so any
  // leftover here is by definition a truncation (gmail.c / gmail.co / etc.).
  // Length guard keeps us from flagging "gmail.cz" etc. in the future.
  return lastLabel.length <= 2;
};

/**
 * "Unreliable" From-header domains: generic email providers (forwarded mail),
 * POS processors / receipt aggregators (sender is not the merchant), junk
 * parses (truncated like "g", "gma", "gmail.c"), or anything < 4 chars /
 * with no dot. When this returns true, the From-header domain MUST NOT be
 * trusted as the merchant identity; downstream code should prefer the
 * body-extracted merchantDomain returned by the LLM.
 */
const isUnreliableHeaderDomain = domain => {
  const normalized = normalizeReceiptDomain(domain);
  if (!normalized) return true;
  if (normalized.length < 4) return true;
  if (!normalized.includes('.')) return true;
  if (isGenericEmailProviderDomain(normalized)) return true;
  if (isSenderNotMerchantDomain(normalized)) return true;
  if (isTruncatedGenericProvider(normalized)) return true;
  return false;
};

module.exports = {
  NEVER_RETAIL_DOMAINS,
  GENERIC_EMAIL_PROVIDER_DOMAINS,
  MULTI_BRAND_MARKETPLACE_DOMAINS,
  SENDER_NOT_MERCHANT,
  normalizeReceiptDomain,
  isNeverRetailDomain,
  isGenericEmailProviderDomain,
  isSenderNotMerchantDomain,
  isTruncatedGenericProvider,
  isUnreliableHeaderDomain
};
